import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { format, differenceInMinutes } from 'date-fns';
import { AppBar, Box, Button, IconButton, Popover, Stack, Tab, Tabs, TextField, Toolbar, Typography, Chip, Tooltip, Menu, MenuItem, CircularProgress, Skeleton } from '@mui/material';
import { Share, KeyboardBackspace, ContentPaste, Drafts, Send, AdsClick, Cancel, AirlineStops, ScheduleSend, JoinInner, Group, Email } from '@mui/icons-material';
import { green, orange, red, pink } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';
import buildUrl from '../buildUrl';
import useInterval from '../hooks/useInterval';
import usePrevious from '../hooks/usePrevious';
import useApi from '../hooks/useApi';
import useSnackbar from '../hooks/useSnackbar';
import LoadingOverlay from '../loadingOverlay/LoadingOverlay';
import NewLinks from './views/NewLinks';
import Hotspot from './views/Hotspot';
import Now from './views/Now';
import NewDevice from './views/NewDevice';
import Channels from './views/Channels';
import NewData from './views/NewData';
import NewSummary from './views/NewSummary';
import TypedValue from '../TypedValue';
import CustomChip from './CustomChip';
import TopicsList from './TopicsList';

const appBarPosition = 112;

const Audience = ({ label, icon, onClick, showMoreInfo, groups, selectedGroup, onClickGroup }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    if (showMoreInfo) {
        return (
            <>
                <CustomChip
                    label={label}
                    icon={icon}
                    onClick={event => setAnchorEl(event.currentTarget)}
                    isMenu={true}
                />
                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={() => setAnchorEl(null)}
                >
                    {groups.map((g, i) => (
                        <MenuItem
                            key={i}
                            onClick={() => onClickGroup(g.id)}
                            selected={selectedGroup === g.id}
                        >
                            {g.name}
                        </MenuItem>
                    ))}
                </Menu>
            </>
        );
    }

    return (
        <CustomChip
            label={label}
            icon={icon}
            onClick={onClick}
        />
    );
};

const QuickStatButton = ({ onClick, text, figure, icon }) => {
    return (
        <Button
            onClick={onClick}
            startIcon={icon}
            size="small"
            variant="text"
            sx={{ whiteSpace: 'pre' }}
        >
            {text}: <TypedValue value={figure} type="number" accuracy={0} />
        </Button>
    );
};

const EmailInfo = ({ email, onFilterByGroup, selectedGroup }) => {
    const [audience, setAudience] = useState(null);
    const [audienceLabel, setAudienceLabel] = useState(null);

    const sendDateTime = format((new Date(email.sendDateTime)), 'HH:mm dd/MM/yyyy');
    const statusAndDate = `${email.status} ${sendDateTime}`;

    const statusIcon = email.status === 'Scheduled'
        ? <ScheduleSend fontSize="inherit" />
        : email.status === 'Sending'
            ? <CircularProgress size={16} color="inherit" />
            : <Send fontSize="inherit" />;

    const handleGetAudience = () => {
        if (email.allSubscribers) {
            setAudience({
                name: 'All Contacts',
                id: null,
                type: 'all'
            });

            return;
        }

        if (email.segment) {
            setAudience({
                name: `Segment: ${email.segment.name}`,
                id: email.segment.id,
                type: 'segment'
            });

            return;
        }

        if (email.groups && email.groups.length > 0) {
            if (email.groups.length === 1) {
                const type = email.groups[0].type;

                if (type === 'manual') {
                    setAudience({
                        name: 'Manual List',
                        id: null,
                        type: 'manual'
                    });

                    return;
                }

                if (type === 'proof') {
                    setAudience({
                        name: 'Proof Group',
                        id: null,
                        type: 'proof'
                    });

                    return;
                }

                setAudience({
                    name: `Group: ${email.groups[0].name}`,
                    id: email.groups[0].id,
                    type: 'group'
                });

                return;
            }

            setAudience({
                name: `${email.groups.length} groups`,
                id: null,
                type: 'group'
            });
        }

        setAudience({
            name: 'Unknown',
            id: null,
            type: null
        });
    };

    const audienceIcon = email.segment
        ? <JoinInner fontSize="inherit" />
        : <Group fontSize="inherit" />;

    const showMoreInfo = (email.allSubscribers || email.segment || email.groups.length === 1 || audience?.type === null) ? false : true;

    useEffect(() => {
        handleGetAudience();
    }, [email]);

    useEffect(() => {
        audience && setAudienceLabel(`Audience: ${audience.name}`);
    }, [audience]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                pl: 1,
                gap: 1
            }}
        >
            <CustomChip
                label={email.name}
                icon={<Email fontSize="inherit" />}
            />
            <CustomChip
                label={statusAndDate}
                icon={statusIcon}
            />
            {audience ? (
                <Audience
                    label={audienceLabel}
                    icon={audienceIcon}
                    onClick={(audience.id !== null && audience.type === 'group') ? () => onFilterByGroup(audience.id) : undefined}
                    onClickGroup={id => onFilterByGroup(id)}
                    showMoreInfo={showMoreInfo}
                    groups={email.groups}
                    selectedGroup={selectedGroup}
                />
            ) : <Skeleton width={200} />}
            <TopicsList topics={email.topics} />
        </Box>
    );
};

const ShareField = ({ value, label }) => {
    const { showSnackbar } = useSnackbar();

    const copyToClipboard = value => {
        navigator.clipboard.writeText(value);
        showSnackbar('Share link copied to clipboard', 'info');
    };

    return (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <TextField
                fullWidth
                value={value}
                label={label}
                onFocus={e => e.target.select()}
                slotProps={{
                    input: {
                        readOnly: true
                    }
                }}
            />
            <IconButton
                aria-label="copy to clipboard"
                onClick={() => copyToClipboard(value)}
                sx={{ flex: 0, marginLeft: 1 }}
            >
                <ContentPaste />
            </IconButton>
        </Stack>
    );
};

const ShareButton = ({ shareLink }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    return (
        <>
            <IconButton onClick={handleClick} edge="end" size="large">
                <Share />
            </IconButton>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
            >
                <Stack spacing={2} sx={{ padding: 2, minWidth: 400 }}>
                    <Typography>Share your campaign online:</Typography>
                    <ShareField value={`${shareLink}?type=facebook`} label="Facebook" />
                    <ShareField value={`${shareLink}?type=twitter`} label="Twitter" />
                    <ShareField value={`${shareLink}?type=linkedin`} label="LinkedIn" />
                    <ShareField value={shareLink} label="Share Link" />
                </Stack>
            </Popover>
        </>
    );
};

const NewEmail = () => {
    const { id } = useParams();
    const { handleGet } = useApi();
    const { showSnackbar } = useSnackbar();
    const theme = useTheme();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialising, setIsInitialising] = useState(true);
    const [html, setHtml] = useState(null);
    const [links, setLinks] = useState(null);
    const [email, setEmail] = useState(null);
    const [summary, setSummary] = useState(null);
    const [groups, setGroups] = useState(null);
    const [tab, setTab] = useState(0);
    const [selectedGroup, setSelectedGroup] = useState('none');
    const [subscriberStatus, setSubscriberStatus] = useState('selected');
    const [linkId, setLinkId] = useState(null);
    const [recentActions, setRecentActions] = useState(null);
    const [pollingInterval, setPollingInterval] = useState(60000);
    const [pollingActive, setPollingActive] = useState(false);
    const [working, setWorking] = useState(false);
    const [refreshDateTime, setRefreshDateTime] = useState(null);
    const previousRecentActions = usePrevious(recentActions);

    const getInterval = date => {
        if (!date) {
            return null;
        }

        let interval = 60000;

        const sendDateTime = new Date(date);
        const now = new Date();

        const difference = differenceInMinutes(sendDateTime, now);

        // between 0 and 24 hours
        if (difference >= 0 && difference <= 120) {
            interval = 10000;
        }

        // more than 2 hours, less than 24
        if (difference > 120 && difference < 1440) {
            interval = 30000;
        }

        setPollingInterval(interval);

        return difference;
    };

    const handleFetchRecentActions = async () => {
        if (!email) {
            return;
        }

        setWorking(true);

        const url = buildUrl(`reports/${email.id}/actions-grouped`, { hour: 1 });
        const response = await handleGet(url);

        if (!response.ok) {
            return;
        }

        setRecentActions(await response.json());
        setRefreshDateTime(new Date());
        setWorking(false);
    };

    const tabs = [
        { label: 'Summary', disabled: false, badge: false },
        { label: 'Real-Time Activity', disabled: false, badge: true },
        { label: 'Hotspot', disabled: !html, badge: false },
        { label: 'Links', disabled: !links, badge: false },
        { label: 'Data', disabled: false, badge: false },
        { label: 'Device', disabled: false, badge: false },
        { label: 'Channels', disabled: false, badge: false },
        { label: 'Print', disabled: true, badge: false }
    ];

    const previousLinkId = usePrevious(linkId);
    const previousEmail = usePrevious(email);

    const handleLoadCampaign = async () => {
        const response = await handleGet(`reports/${id}`);

        if (!response.ok) {
            showSnackbar('Error fetching Email', 'error');
            return;
        }

        setEmail(await response.json());
    };

    const handleChangeTab = tabName => {
        const newTabIndex = tabs.map(e => e.label).indexOf(tabName);

        newTabIndex && setTab(newTabIndex);
    };

    const handleLoadCampaignSummary = async () => {
        const url = buildUrl(`reports/${id}/summary`, {
            version: 2,
            ...(selectedGroup !== 'none' && { groupId: selectedGroup })
        });

        const response = await handleGet(url);

        if (!response.ok) {
            showSnackbar('Error fetching Summary', 'error');
            return;
        }

        setSummary(await response.json());
    };

    const handleFetchGroups = async () => {
        const response = await handleGet('groups');

        if (!response.ok) {
            showSnackbar('Error fetching Groups', 'error');
            return;
        }

        setGroups(await response.json());
    };

    const handleFetchHtml = async () => {
        const response = await handleGet(`reports/${id}/html`);

        if (!response.ok) {
            showSnackbar('Error fetching HTML', 'error');
            return;
        }

        setHtml(await response.text());
    };

    const handleFetchLinks = async () => {
        const response = await handleGet(`reports/${id}/links`);

        if (!response.ok) {
            showSnackbar('Error fetching Links', 'error');
            return;
        }

        setLinks(await response.json());
    };

    const handleChangeSubscriberStatusFilter = subscriberStatus => {
        handleChangeTab('Data');
        setSubscriberStatus(subscriberStatus);
    };

    const handleFilterByGroup = id => {
        setSelectedGroup(id);
        handleChangeTab('Data');
    };

    useEffect(() => {
        if (!email) {
            return;
        }

        if (email !== previousEmail) {
            setSelectedGroup('none');
        }
    }, [email]);

    useEffect(() => {
        if (!isInitialising) {
            setIsLoading(true);
            setEmail(null);
            setSummary(null);
        }

        handleLoadCampaign();
        handleLoadCampaignSummary();
        handleFetchGroups();
        handleFetchHtml();
        handleFetchLinks();
    }, [id]);

    useEffect(() => {
        if (email && summary && groups) {
            setIsLoading(false);
            setIsInitialising(false);
        }
    }, [email, summary, groups]);

    useEffect(() => {
        if (isInitialising) {
            return;
        }

        setIsLoading(true);
        setSummary(null);

        handleLoadCampaignSummary();
    }, [selectedGroup]);

    useEffect(() => {
        if (!isInitialising && linkId && (linkId !== previousLinkId)) {
            handleChangeTab('Links');
        }
    }, [linkId]);

    useEffect(() => {
        if (isLoading || isInitialising || recentActions?.actions.length === 0) {
            return;
        }

        const current = JSON.stringify(recentActions.actions.sort((a, b) => b.type - a.type));
        const previous = JSON.stringify(previousRecentActions?.actions.sort((a, b) => b.type - a.type));

        // if any new types of action, or counts different, refetch main data to match polled data
        if (current !== previous) {
            handleLoadCampaign();
        }
    }, [recentActions]);

    const intervalRef = useInterval(() => {
        handleFetchRecentActions();

        return () => {
            clearInterval(intervalRef.current);
        };
    }, pollingActive ? pollingInterval : null);

    useEffect(() => {
        if (!email) {
            return;
        }

        const campaignAgeInMinutes = getInterval(email.sendDateTime);

        const campaignIsLessThan2weeksOld = campaignAgeInMinutes < 20160;

        handleFetchRecentActions();
        setPollingActive(campaignIsLessThan2weeksOld);
    }, [email]);

    if (isLoading || isInitialising) {
        return (
            <LoadingOverlay />
        );
    }

    return (
        <>
            <AppBar
                position="sticky"
                color="inherit"
                sx={{
                    top: appBarPosition,
                    zIndex: theme.zIndex.appBar - 1,
                    borderTop: 1,
                    borderColor: theme.palette.divider
                }}
            >
                <Toolbar variant="dense" sx={{ overflow: 'hidden', pt: 2, pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                        <Tooltip title="Return to Dashboard">
                            <IconButton onClick={() => navigate(-1)}>
                                <KeyboardBackspace fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <EmailInfo
                            email={email}
                            onFilterByGroup={handleFilterByGroup}
                            selectedGroup={selectedGroup}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            rowGap: 0,
                            columnGap: 1
                        }}
                    >
                        <QuickStatButton
                            onClick={() => handleChangeSubscriberStatusFilter('sent')}
                            text="Sent"
                            figure={email.sent}
                            icon={<Send sx={{ color: green[500] }} />}
                        />
                        <QuickStatButton
                            onClick={() => handleChangeSubscriberStatusFilter('opened')}
                            text="Unique Opens"
                            figure={email.uniqueOpens}
                            icon={<Drafts sx={{ color: green[500] }} />}
                        />
                        <QuickStatButton
                            onClick={() => handleChangeSubscriberStatusFilter('clicked')}
                            text="Unique Clicks"
                            figure={email.uniqueClicks}
                            icon={<AdsClick sx={{ color: green[500] }} />}
                        />
                        <QuickStatButton
                            onClick={() => handleChangeSubscriberStatusFilter('bounced')}
                            text="Bounced"
                            figure={email.bounced}
                            icon={<AirlineStops fontSize="small" sx={{ color: orange[500] }} />}
                        />
                        {email.failed > 0 && (
                            <QuickStatButton
                                onClick={() => handleChangeSubscriberStatusFilter('failed')}
                                text="Failed"
                                figure={email.failed}
                                icon={<Cancel fontSize="small" sx={{ color: red[500] }} />}
                            />
                        )}
                    </Box>
                </Toolbar>
                <Toolbar variant="dense">
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="caption">
                            Subject:
                        </Typography>
                        <Typography
                            variant="h5"
                            sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                fontSize: '1.4em',
                                lineHeight: 1.1
                            }}
                        >
                            {email.subject}
                        </Typography>
                    </Box>
                    <ShareButton shareLink={email.shareLink} />
                </Toolbar>
                <Tabs value={tab} onChange={(e, tab) => setTab(tab)}>
                    {tabs.map((e, i) => {
                        const recentActionCount = recentActions?.actions?.reduce((prev, cur) => prev + cur.total, 0) ?? 0;

                        const style = recentActionCount > 0 ? { backgroundColor: pink[500], color: '#FFF' } : null;

                        const label = e.badge
                            ? (
                                <Box
                                    component="span"
                                    sx={{ whiteSpace: 'pre' }}
                                >
                                    {e.label}
                                    <Chip
                                        label={recentActionCount}
                                        sx={{
                                            ml: 1,
                                            ...(style && style)
                                        }}
                                        size="small"
                                    />
                                </Box>
                            )
                            : e.label;

                        return (
                            <Tab
                                key={i}
                                label={label}
                                disabled={e.disabled}
                            />
                        );
                    })}
                </Tabs>
            </AppBar>
            <Box sx={{ overflow: 'hidden' }}>
                {tab === 0 && (
                    <NewSummary
                        email={email}
                        onChangeSubscriberStatusFilter={subscriberStatus => handleChangeSubscriberStatusFilter(subscriberStatus)}
                        onChangeTab={handleChangeTab}
                    />
                )}
                {tab === 1 && (
                    <Now
                        appBarPosition={appBarPosition}
                        data={recentActions}
                        onFetchData={handleFetchRecentActions}
                        pollingInterval={pollingInterval}
                        pollingActive={pollingActive}
                        onTogglePolling={setPollingActive}
                        refreshDateTime={refreshDateTime}
                        working={working}
                        sendDateTime={email.sendDateTime}
                    />
                )}
                {tab === 2 && (
                    <Hotspot
                        html={html}
                        itemLinks={links}
                        onViewWhoClicked={setLinkId}
                    />
                )}
                {tab === 3 && (
                    <NewLinks
                        id={email.id}
                        allLinks={links}
                        onViewWhoClicked={setLinkId}
                        linkId={linkId}
                        onRefreshGroups={handleFetchGroups}
                    />
                )}
                {tab === 4 && (
                    <NewData
                        campaignId={email.id}
                        groups={groups}
                        subscriberStatus={subscriberStatus}
                        setSubscriberStatus={subscriberStatus => setSubscriberStatus(subscriberStatus)}
                        selectedGroup={selectedGroup}
                        setSelectedGroup={selectedGroup => setSelectedGroup(selectedGroup)}
                        campaignSummary={summary}
                        onRefreshGroups={handleFetchGroups}
                    />
                )}
                {tab === 5 && (
                    <NewDevice
                        id={email.id}
                        onRefreshGroups={handleFetchGroups}
                    />
                )}
                {tab === 6 && (
                    <Channels
                        id={email.id}
                        uniqueOpens={email.uniqueOpens}
                    />
                )}
            </Box>
        </>
    );
};

export default NewEmail;